import { ref, nextTick, type Ref } from 'vue';

import type { Label } from './types';

// Constants
const PADDING_MARGIN = 32;

export function useLabelsOverflow(
  containerRef: Ref<HTMLElement | null>,
  initialLabels: Label[] = [],
  isCharterFlight: Ref<boolean | undefined> = ref(false),
  charterLabelRef: Ref<HTMLElement | null> = ref(null),
) {
  const visibleLabels = ref<Label[]>([...initialLabels]);

  /**
   * Checks if labels overflow the container and adjusts visible labels accordingly
   * @param labels Current labels array
   */
  const checkLabelsOverflow = async (labels: Label[]) => {
    if (labels.length === 0) {
      return;
    }

    visibleLabels.value = [...labels];

    await nextTick();

    if (!containerRef.value || !containerRef.value.parentElement) return;

    const availableWidth =
      containerRef.value.parentElement.clientWidth - PADDING_MARGIN;

    const { children } = containerRef.value;

    if (!children || children.length === 0) {
      return;
    }

    let occupiedWidth = 0;
    let fitCount = 0;

    // If charter flight exists, it's the first child - account for its width
    let startIndex = 0;
    if (isCharterFlight.value && charterLabelRef.value) {
      const charterEl = charterLabelRef.value as HTMLElement;
      const styles = window.getComputedStyle(charterEl);
      const marginLeft = parseFloat(styles.marginLeft) || 0;
      occupiedWidth = charterEl.offsetWidth + marginLeft;
      startIndex = 1; // Skip charter label in the loop, start from next element
    }

    // Check remaining labels starting from startIndex
    for (let i = startIndex; i < children.length; i++) {
      const el = children[i] as HTMLElement;
      const styles = window.getComputedStyle(el);
      const marginLeft = parseFloat(styles.marginLeft) || 0;
      const elementWidth = el.offsetWidth + marginLeft;

      if (occupiedWidth + elementWidth <= availableWidth) {
        occupiedWidth += elementWidth;
        fitCount++;
      } else {
        break;
      }
    }

    if (fitCount < labels.length) {
      visibleLabels.value = labels.slice(0, fitCount);
    }
  };

  return {
    visibleLabels,
    checkLabelsOverflow,
  };
}
